import type { WalletDetailResult } from "@/lib/api";

/**
 * On-chain wallet balances from `GET /v1/wallets/{id}` (ALL-4839).
 *
 * The gateway returns `balanceStatus`:
 *  - `OK`          — a complete chain read; an empty `balances` list really means zero.
 *  - `UNAVAILABLE` — the chain read failed; balances are unknown and
 *                    `balanceSupportReference` names the server log line.
 *  - `UNSUPPORTED` — no balance source covers this wallet's network.
 *
 * "Unknown" must never render as "No assets found": that is the bug this module exists to prevent.
 */
export type WalletBalanceStatus = "ok" | "unavailable" | "unsupported";

export interface WalletBalanceView {
  symbol: string;
  name: string;
  /** Plain decimal string, e.g. "0.05". */
  amount: string;
}

export interface WalletBalancesResult {
  status: WalletBalanceStatus;
  balances: WalletBalanceView[];
  supportReference?: string;
}

export function isNonZeroBalance(balance: Record<string, unknown>): boolean {
  // A malformed gateway payload can carry a null/non-object entry in `balances`.
  // Such an entry is unknown, not zero, so it is dropped rather than thrown on.
  if (!balance || typeof balance !== "object") return false;
  const atomic = balance.balanceAtomic;
  if (typeof atomic === "string" && /^\d+$/.test(atomic)) {
    return /[1-9]/.test(atomic);
  }
  const amount = String(balance.amount ?? balance.balance ?? "");
  return /^\d*\.?\d+$/.test(amount) && /[1-9]/.test(amount);
}

function nonBlank(value: unknown): string | undefined {
  return typeof value === "string" && value.trim() ? value.trim() : undefined;
}

export function readWalletBalances(result: WalletDetailResult): WalletBalancesResult {
  if (!result.wallet || result.source === "list") {
    return { status: "unavailable", balances: [], supportReference: result.supportReference };
  }
  const raw = result.wallet;
  const status = nonBlank(raw.balanceStatus)?.toUpperCase();
  const supportReference = nonBlank(raw.balanceSupportReference);
  if (status === "UNSUPPORTED") {
    return { status: "unsupported", balances: [], supportReference };
  }
  // A legacy detail may carry real balance rows without balanceStatus. Keep
  // those rows, but never infer an empty wallet from an absent status/list.
  const hasLegacyRows = status === undefined && Array.isArray(raw.balances) && raw.balances.length > 0;
  if ((status !== "OK" && !hasLegacyRows) || raw.degraded === true || !Array.isArray(raw.balances)) {
    return { status: "unavailable", balances: [], supportReference };
  }
  const list = Array.isArray(raw.balances) ? (raw.balances as Record<string, unknown>[]) : [];
  const balances = list.filter(isNonZeroBalance).map((b) => {
    const symbol = String(b.symbol ?? b.asset ?? "");
    return {
      symbol,
      name: String(b.name ?? symbol),
      amount: String(b.amount ?? b.balance ?? "0"),
    };
  });
  return { status: "ok", balances };
}
